import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys, type VideoCard as VideoCardPayload } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { publicApi } from '@/lib/api-client';
import { STALE } from '@/lib/query';
import { useVideos } from '@/lib/use-videos';
import { VideoCard } from '@/components/video';
import { CardGridSkeleton, PageHeader, Section } from './page-parts';
import { EmptyState, ErrorState, LoadMore, Tabs } from '@/components/ui';

/**
 * `/:locale/videos` — PRD §13.
 *
 * Two decisions worth naming:
 *
 * - **The category filter lives in the URL**, like search's does (Phase 10), so a
 *   filtered grid is a link someone can send rather than a state a reader has to
 *   recreate.
 * - **Shorts get their own row.** They are 9:16 and everything else is 16:9; mixed into
 *   one grid either the Shorts are letterboxed or the grid rows are ragged. A separate
 *   horizontal row is how every video product solves this, and it is what the Phase 12
 *   task list asks for.
 */
export default function VideosPage() {
  const { locale, t } = useLocale();
  const [params, setParams] = useSearchParams();
  const category = params.get('category') ?? undefined;

  const { data: categories } = useQuery({
    queryKey: queryKeys.categories(locale),
    queryFn: () => publicApi.categories(locale),
    staleTime: STALE.taxonomy,
  });

  const { videos, isPending, isError, hasNextPage, isFetchingNextPage, fetchNextPage, refetch } =
    useVideos(locale, category);

  const shorts = videos.filter((video) => video.source === 'youtube_short');
  const standard = videos.filter((video) => video.source !== 'youtube_short');

  const tabs = [
    { id: '', label: t('filter.all') },
    ...(categories ?? []).map((row) => ({ id: row.slug, label: row.name })),
  ];

  const select = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set('category', id);
    else next.delete('category');
    setParams(next);
  };

  return (
    <>
      <PageHeader title={t('section.videos')} subtitle={t('video.indexBlurb')} />

      <Section>
        <Tabs
          items={tabs}
          value={category ?? ''}
          onChange={select}
          label={t('video.filterLabel')}
          className="mb-6"
        />

        {isError ? (
          <ErrorState
            title={t('error.genericTitle')}
            body={t('error.genericBody')}
            retryLabel={t('action.retry')}
            onRetry={() => void refetch()}
          />
        ) : isPending ? (
          <CardGridSkeleton count={8} label={t('status.loading')} />
        ) : videos.length === 0 ? (
          <EmptyState title={t('video.none')} />
        ) : (
          <>
            {shorts.length > 0 && <ShortsRow shorts={shorts} />}

            {standard.length > 0 && (
              <>
                <h2 className="sr-only">{t('section.videos')}</h2>
                <div className="grid grid-cols-1 gap-4.5 sm:grid-cols-2 lg:grid-cols-4">
                  {standard.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </>
            )}

            <LoadMore
              className="mt-8"
              hasMore={hasNextPage}
              loading={isFetchingNextPage}
              onLoad={() => void fetchNextPage()}
              label={t('action.loadMore')}
            />
          </>
        )}
      </Section>
    </>
  );
}

/**
 * The vertical-format row. It scrolls sideways rather than wrapping, which keeps the
 * Shorts a band across the top of the page instead of a wall of portrait cards — and
 * `snap-x` makes that band usable with a thumb.
 */
function ShortsRow({ shorts }: { shorts: VideoCardPayload[] }) {
  const { t } = useLocale();
  return (
    <section className="mb-8" aria-labelledby="nk-shorts">
      <h2 id="nk-shorts" className="mb-3 text-lg font-extrabold text-ink">
        {t('video.shorts')}
      </h2>
      <div className="flex snap-x gap-4 overflow-x-auto pb-2" data-shorts-row>
        {shorts.map((video) => (
          <VideoCard key={video.id} video={video} variant="short" className="snap-start" />
        ))}
      </div>
    </section>
  );
}
